import React, { useEffect, useRef, useState  } from "react";
import { Box } from "@mui/material";
import MagicIcon from "@mui/icons-material/AutoAwesome";
import { Widget, addResponseMessage, toggleWidget, isWidgetOpened, dropMessages, setQuickButtons, renderCustomComponent, toggleMsgLoader } from "react-chat-widget";
import 'react-chat-widget/lib/styles.css';
import './chatWidget.scss'; // custom style overrides
import { flattenData, gptResponse } from "../../../services/openApi.service";
import serverFunctions from "../../asheetPlugin/utils2/serverFunctions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";


export const ChatWidget = (props) => {

  const { connection, selectedConnection } = props;
  const resultRef = useRef(null);
  const [widgetOpen, setWidgetOpen] = useState(false);

  useEffect(() => {
    dropMessages();
    addResponseMessage(`Hello! I’m your AI assistant for ${selectedConnection?.description}. How can I help?`);
  }, []);

  const handleNewUserMessage = async (message) => {
    try {
      toggleMsgLoader();
      setQuickButtons([]);
      const requestPayload = {
        connectionUId: connection?.uId ?? "",
        prompt: message,
      };

      const response = await gptResponse(requestPayload);
      const data = response?.data;

      const { applicationLogic, result, errorMessage } = data;

      let finalFlattenedData = null;
      if (errorMessage) {
        addResponseMessage(errorMessage);
      }
      else if (applicationLogic) {
        const transformed = handleTransformationScript(applicationLogic, result?.data);
        console.log(transformed, "transformed Data");
        if (typeof transformed !== "undefined") {
          const normalized = Array.isArray(transformed) ? transformed : [transformed];

          if (hasNestedObjects(normalized)) {
            console.log("Flattening nested transformed data...");
              finalFlattenedData = await handleFlattenData(normalized);
          }

          finalFlattenedData = finalFlattenedData ? finalFlattenedData : normalized

          formatTransformedResponse(finalFlattenedData);
          showRenderQuickButton(finalFlattenedData);
        } else {
          addResponseMessage("Oops! Something went wrong..");
        }
      } else {
        const rawData = result?.data;
        console.log(rawData, "rawData");

        if (hasNestedObjects(rawData)) {
          console.log("Flattening nested transformed data...");
            finalFlattenedData = await handleFlattenData(rawData);
        }
        finalFlattenedData = finalFlattenedData ? finalFlattenedData : rawData

        formatTransformedResponse(finalFlattenedData)
        showRenderQuickButton(finalFlattenedData);
      }
    } catch (error) {
      addResponseMessage("Oops! Something went wrong.");
    } finally {
      toggleMsgLoader();
    }
  };

  const handleTransformationScript = (script, testedDataList) => {
    try {
      const dataTransformation = eval(`(${script})`);
      return dataTransformation(testedDataList);
    } catch (error) {
      console.error("Script error:", error);
      return undefined;
    }
  };

  const formatTransformedResponse = async (result) => {
    if (Array.isArray(result)) {
      if (result.length === 1 && typeof result[0] === "object") {

        console.log("Custom Table render started")
        const rendered = renderCustomComponent(SingleRowTable, { rowData: result[0] }, false);
        console.log(rendered, "Custom Table rendered")

        return JSON.stringify(result, null, 2);

      } else {
        return JSON.stringify(result, null, 2);
      }
    } else if (typeof result === "object") {
      return JSON.stringify(result, null, 2);
    }
  
    return String(result);
  };

  const showRenderQuickButton = (result) => {
    resultRef.current = result;
    setQuickButtons([]); 
      
    if (!Array.isArray(result) || result.length === 0 || typeof result[0] !== "object") {
      addResponseMessage("Unable to process Data, Please try again");
      return;
    }

    const dataLengthLabel =  result?.length > 1 ? `Total ${result?.length} Rows Returned-` : ""

    addResponseMessage("✅ Data is ready.");
    setQuickButtons([{ label: `${dataLengthLabel}Render to Sheet`, value: "render_to_sheet" } ]);
  };

  const handleQuickButtonClicked = async (value) => {
    if (value === "render_to_sheet") {
      addResponseMessage("📝 Rendering to sheet...");
  
      try {
        const result = resultRef.current;
  
        if (!Array.isArray(result) || result.length === 0 || typeof result[0] !== "object") {
          addResponseMessage("Unable to process Data, Please try again");
          return;
        }
  
        const data = JSON.stringify(result);
        const headers = Object.keys(result[0]).map(key => ({
          columnName: key,
          displayName: key,         
          displayFormat: "General"
        }));

        const sheetName = `${selectedConnection?.description}-AssistantSheet`;
        const collectionUId = "";
        const isRenderInSameSheet = false;
        const connectionName = "";
        const isDataFromOtherSource = true;

        console.log(headers, "headers for sheet")
        console.log(result, "Data for sheet")

        const response = await serverFunctions.sendDataToSheet(
          headers,
          data,
          sheetName,
          null,
          collectionUId,
          connectionName,
          isRenderInSameSheet,
          null,
          isDataFromOtherSource
        );
  
        console.log("Sheet response:", response);
        if (response?.success) {
          addResponseMessage("✅ Data has been rendered to the sheet.");
          setQuickButtons([]);
        }
      } catch (error) {
        console.error("Render error:", error);
        addResponseMessage("❌ Failed to render data to the sheet.");
      }
    }
  };
  
  const handleToggleWidget = () => {
    const nextOpen = !widgetOpen;
    setWidgetOpen(nextOpen);
    toggleWidget();
  };
  
  const handleCloseWidget = () => {
    setWidgetOpen(false);
    toggleWidget(); // closes it
  };

  const hasNestedObjects = (data) => {
    if (Array.isArray(data)) {
      return data.some(item => 
        typeof item === 'object' && item !== null &&
        Object.values(item).some(value => typeof value === 'object' && value !== null)
      );
    } else if (typeof data === 'object' && data !== null) {
      return Object.values(data).some(value => typeof value === 'object' && value !== null);
    }
    return false;
  };

  const handleFlattenData = async (data) => {

    const flattenedData = await flattenData(data);

    let finalData = flattenedData?.data;
    finalData = Object.keys(finalData).reduce((acc, key) => {
      if (key === 'RowIndex' || key === 'Level' || key?.includes("ParentRowIndex")) return acc;

      finalData[key].forEach((value, index) => {
        if (!acc[index]) {
          acc[index] = {};
        }
        acc[index][key] = value;        
      });
      return acc;
    }, []);

    return finalData;
  }

  return (
    <>
      {/* Custom Toggle Button */}
      {!widgetOpen && (
        <Box
          onClick={() => handleToggleWidget()}
          sx={{
            position: "fixed",
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: "pointer",
            padding: "16px",
          }}
        >
          <MagicIcon color="primary" />
        </Box>
      )}

      {widgetOpen && (
        <Box
          className="chat-back-icon"
          onClick={() => handleCloseWidget()}
        >
          <ArrowBackIcon/>
        </Box>
      )}

      {/* Chat Widget */}
      <Widget
        handleNewUserMessage={handleNewUserMessage}
        title={`${selectedConnection?.description} AI Assistant`}
        showCloseButton={false}
        fullScreenMode={true}
        handleQuickButtonClicked={handleQuickButtonClicked}
        senderPlaceHolder="Type a prompt..."
        showTimeStamp={false}
        autofocus={true}
      />
    </>
  );
};


// SingleRowTable.jsx
const tableStyle = {
  borderCollapse: 'collapse',
  width: '100%',
  background: "white",
};

const tdStyle = {
  border: '1px solid #ddd',
  padding: '4px',
  textAlign: 'left',
};

const tbStyle = {
  fontSize: '11px'
};

export const SingleRowTable = ({ rowData }) => {
  if (!rowData || typeof rowData !== 'object') return null;

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%', minWidth:"225px" }}>
      <table style={tableStyle}>
        <tbody style={tbStyle}>
          {Object?.entries(rowData)?.map(([key, value]) => (
            <tr key={key}>
              <td style={{ ...tdStyle, fontWeight: 'bold' }}>{key}</td>
              <td style={tdStyle}>{String(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};